import { toast } from 'sonner';
import {
    Chart,
    Edit2,
    Image as ImageIcon,
    Sparkles,
    Trash,
    type IconComponent,
} from '@/components/icons';
import { getBlockRealId } from '@/components/preview/block-selection/blockClipboard';
import { useTestBlockSelection } from '@/components/preview/block-selection/TestBlockSelectionContext';
import { stripSplitBlockId } from '@/components/preview/pagination/utils';
import { useConfirmDelete } from '@/hooks/use-confirm-delete';
import { cn } from '@/lib/utils';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import type { TestBlock } from '@/types/report-payload';

interface BlockAction {
    id: string;
    label: string;
    icon: IconComponent;
    onClick: () => void;
    /** Renders in red and sits last. */
    destructive?: boolean;
    /** Greyed with a reason in the title; the click still explains itself. */
    blockedReason?: string | null;
}

interface BlockActionsPanelProps {
    block: TestBlock;
    /** The step's whole block list. Only the delete confirmation needs it: what
     *  a table takes down with it is a property of the list, not of the block. */
    blocks: TestBlock[];
    /** Same entry point the toolbox tiles use; `tool_chart` opens the chart
     *  modal pre-pointed at the scoped table. */
    onAddBlock?: (toolId: string, scope?: { anchorRealId: string | null }) => void;
}

/**
 * What each block is called *in a laudo*, which is not what the payload calls
 * it. A psychologist reading the WISC-IV row of scores does not see a table,
 * they see the instrument — the same word the battery list, the entry point and
 * `blockGroups`' "Instrumento sem nome" fallback already use. Only this panel
 * still spoke the schema's language.
 */
const BLOCK_KIND_LABEL: Record<string, string> = {
    table: 'Instrumento',
    text: 'Texto',
    chart: 'Gráfico',
    image: 'Imagem',
    divider: 'Divisor de seção',
};

/**
 * A text block tied to a table is the instrument's interpretation, never a
 * loose paragraph — the delete confirmation below has always called it that.
 * Free-standing text keeps the generic name because that is all it is.
 */
const resolveKindLabel = (block: TestBlock): string => {
    if (block.block_type === 'text' && block.linked_table_id) return 'Interpretação';
    return BLOCK_KIND_LABEL[block.block_type] ?? 'Bloco';
};

/**
 * The tools panel's inner level: what you can do with the block you just
 * clicked. Every action here already existed — behind a hover rail positioned
 * outside the page, a right-click menu, or a keyboard shortcut with no visible
 * control. Usability testing showed people find none of them, so this is the
 * one surface that is always on screen while the step is open.
 *
 * The rail and the context menus stay as accelerators; this is not a
 * replacement for them, it is the discoverable path to the same handlers.
 */
export default function BlockActionsPanel({ block, blocks, onAddBlock }: BlockActionsPanelProps) {
    const { setPendingBlockAction, setPendingEditBlockId, isReadOnly, isGenerating } =
        useReportWizard();
    const blockSelection = useTestBlockSelection();
    const confirmDelete = useConfirmDelete();

    const realId = stripSplitBlockId(getBlockRealId(block));
    const kind = block.block_type;

    const actions: BlockAction[] = [];

    if (kind === 'table') {
        actions.push({
            id: 'consolidate',
            label: 'Interpretar com IA',
            icon: Sparkles,
            blockedReason: isGenerating
                ? 'A IA está gerando outro conteúdo. Interrompa para interpretar este instrumento.'
                : null,
            onClick: () => setPendingBlockAction({ realId, action: 'consolidate' }),
        });
        actions.push({
            id: 'chart',
            label: 'Gerar gráfico deste instrumento',
            icon: Chart,
            onClick: () => onAddBlock?.('tool_chart', { anchorRealId: realId }),
        });
    }

    if (kind === 'text') {
        actions.push({
            id: 'edit-text',
            label: 'Editar texto',
            icon: Edit2,
            onClick: () => setPendingEditBlockId(realId),
        });
    }

    if (kind === 'chart') {
        actions.push({
            id: 'edit-chart',
            label: 'Editar gráfico',
            icon: Edit2,
            onClick: () => setPendingBlockAction({ realId, action: 'edit-chart' }),
        });
    }

    if (kind === 'image') {
        actions.push({
            id: 'replace-image',
            label: 'Trocar imagem',
            icon: ImageIcon,
            onClick: () => setPendingBlockAction({ realId, action: 'replace-image' }),
        });
    }

    const kindLabel = resolveKindLabel(block);

    actions.push({
        id: 'delete',
        label: 'Excluir bloco',
        icon: Trash,
        destructive: true,
        onClick: () =>
            confirmDelete({
                title: `Excluir ${kindLabel.toLowerCase()}?`,
                confirmText: 'Excluir',
                onConfirm: (close) => {
                    close();
                    blockSelection.deleteBlocks([realId]);
                },
            }),
    });

    return (
        <div className="space-y-3">
            {/* No "Ações —" prefix: the buttons under it are visibly actions,
                and the panel header already names the instrument. All this row
                owes the reader is which part of it they clicked. */}
            <h3 className="text-sm font-semibold text-foreground">{kindLabel}</h3>

            <div className="space-y-2">
                {actions.map((action) => {
                    const Icon = action.icon;
                    const blocked = Boolean(action.blockedReason);

                    return (
                        <button
                            key={action.id}
                            type="button"
                            disabled={isReadOnly}
                            // `aria-disabled` rather than `disabled` for the
                            // blocked case: the click has to survive so it can
                            // say *why*, which is the same rule the hover rail
                            // follows for its AI button.
                            aria-disabled={blocked}
                            title={action.blockedReason ?? undefined}
                            onClick={() => {
                                if (blocked) {
                                    toast.warning(action.blockedReason as string);
                                    return;
                                }
                                action.onClick();
                            }}
                            className={cn(
                                'flex w-full items-center gap-3 rounded-xl border-2 border-border bg-card p-3 text-left transition-all',
                                'disabled:cursor-not-allowed disabled:opacity-50',
                                blocked ? 'opacity-50' : 'hover:bg-surface',
                                action.destructive
                                    ? 'hover:border-destructive/40 hover:bg-destructive/5'
                                    : null,
                            )}
                        >
                            <div
                                className={cn(
                                    'flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface',
                                    action.destructive
                                        ? 'text-destructive'
                                        : 'text-muted-foreground',
                                )}
                            >
                                <Icon className="size-4" />
                            </div>
                            <span
                                className={cn(
                                    'text-sm font-medium',
                                    action.destructive ? 'text-destructive' : 'text-foreground',
                                )}
                            >
                                {action.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
